import { useMemo } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { arSA, enUS } from "date-fns/locale";
import { useLanguage } from "../i18n/LanguageContext";

export const STATUSES = ["new", "under_review", "action_required", "in_proceedings", "resolved", "escalated"];

export const STATUS_STYLES = {
  new: "bg-amber-100 text-amber-800 border-amber-200",
  under_review: "bg-blue-50 text-blue-800 border-blue-200",
  action_required: "bg-orange-50 text-orange-700 border-orange-200",
  in_proceedings: "bg-violet-50 text-violet-700 border-violet-200",
  resolved: "bg-green-50 text-green-700 border-green-200",
  escalated: "bg-red-50 text-red-800 border-red-200",
};

export const STATUS_CHART_COLORS = {
  new: "#F59E0B",
  under_review: "#3B82F6",
  action_required: "#F97316",
  in_proceedings: "#8B5CF6",
  resolved: "#22C55E",
  escalated: "#EF4444",
};

export const PRIORITY_STYLES = {
  low: "bg-slate-100 text-slate-600 border-slate-200",
  medium: "bg-sky-50 text-sky-700 border-sky-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  urgent: "bg-red-50 text-red-700 border-red-200",
};

export const ROLE_STYLES = {
  super_admin: "bg-delta-navy text-white border-delta-navy",
  admin: "bg-indigo-50 text-indigo-800 border-indigo-200",
  lawyer: "bg-delta-limeLight text-[#5c7023] border-delta-limeBorder",
  requester: "bg-slate-100 text-slate-700 border-slate-200",
};

export const LABELS = {
  en: {
    status: {
      new: "Pending Review",
      under_review: "Under Legal Review",
      action_required: "Action Required",
      in_proceedings: "In Legal Proceedings",
      resolved: "Resolved",
      escalated: "Escalated Risk",
    },
    priority: { low: "Low", medium: "Medium", high: "High", urgent: "Urgent" },
    role: { super_admin: "Super Admin", admin: "Admin", lawyer: "Lawyer", requester: "Requester" },
    company: { b2b: "B2B Corporate", outpatient: "Outpatient" },
    type: { complaint: "Complaint", case: "Legal Case" },
    action: {
      case_created: "Case Created",
      status_changed: "Status Updated",
      comment_added: "Comment Added",
      attachment_added: "Attachment Added",
      case_assigned: "Case Assigned",
      case_updated: "Case Updated",
      user_created: "User Created",
      user_updated: "User Updated",
      user_login: "Sign In",
    },
  },
  ar: {
    status: {
      new: "بانتظار المراجعة",
      under_review: "قيد المراجعة القانونية",
      action_required: "يتطلب إجراءً",
      in_proceedings: "في إجراءات قانونية",
      resolved: "تم الحل",
      escalated: "مخاطر متصاعدة",
    },
    priority: { low: "منخفضة", medium: "متوسطة", high: "عالية", urgent: "عاجلة" },
    role: { super_admin: "مشرف عام", admin: "مشرف", lawyer: "محامٍ", requester: "مقدم طلب" },
    company: { b2b: "شركات (B2B)", outpatient: "عيادات خارجية" },
    type: { complaint: "شكوى", case: "قضية قانونية" },
    action: {
      case_created: "إنشاء قضية",
      status_changed: "تحديث الحالة",
      comment_added: "إضافة تعليق",
      attachment_added: "إضافة مرفق",
      case_assigned: "تعيين قضية",
      case_updated: "تحديث قضية",
      user_created: "إنشاء مستخدم",
      user_updated: "تحديث مستخدم",
      user_login: "تسجيل دخول",
    },
  },
};

export function useLabels() {
  const { lang } = useLanguage();
  return useMemo(() => LABELS[lang] || LABELS.en, [lang]);
}

const currentLocale = () =>
  typeof document !== "undefined" && document.documentElement.lang === "ar" ? arSA : enUS;

export const fmtDateTime = (iso) => (iso ? format(new Date(iso), "dd MMM yyyy, HH:mm", { locale: currentLocale() }) : "—");
export const fmtDate = (iso) => (iso ? format(new Date(iso), "dd MMM yyyy", { locale: currentLocale() }) : "—");
export const timeAgo = (iso) => (iso ? formatDistanceToNow(new Date(iso), { addSuffix: true, locale: currentLocale() }) : "—");

export const fmtSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
